import type React from 'react'
import { useRef } from 'react'

/** Fields that own their arrow keys */
const textEntrySelector = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])'

/**
 * Arrow-key shortcuts for a list of rows, on top of the normal Tab order.
 *
 * ArrowUp/ArrowDown move real focus to the previous/next row's open target (`[data-list-row]`, or the row
 * element itself when it has none); Home/End go to the first/last row. There is no wrap at the ends. Real
 * focus, not a highlight attribute, so assistive tech follows along (WCAG 4.1.2 / 2.4.3).
 *
 * The hook does not handle Enter: each row's own control activates. It never prevents a key it does not
 * act on, and it ignores keys from text entry, from a menu open inside a row (or portalled out of it),
 * and keys a row control has already handled.
 */
export const useListKeyboardNav = () => {
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map())

  const getFocusTarget = (row: HTMLElement) =>
    row.matches('[data-list-row]') ? row : (row.querySelector<HTMLElement>('[data-list-row]') ?? row)

  const handleKeyDown = (ev: React.KeyboardEvent<HTMLElement>) => {
    if (ev.defaultPrevented || ev.altKey || ev.ctrlKey || ev.metaKey || ev.shiftKey) {
      return
    }
    const list = ev.currentTarget
    const target = ev.target
    // React bubbles events from portals (menu popups) through the component tree; those are not ours.
    if (!(target instanceof HTMLElement) || !list.contains(target)) {
      return
    }
    if (target.closest(textEntrySelector)) {
      return
    }
    const menu = target.closest('[role="menu"]')
    if (menu && menu !== list && list.contains(menu)) {
      return
    }

    const rows = [...itemRefs.current.entries()].sort(([a], [b]) => a - b).map(([, row]) => row)
    if (rows.length === 0) {
      return
    }
    const current = rows.findIndex(row => row.contains(target))
    const last = rows.length - 1

    const next = (() => {
      switch (ev.key) {
        case 'ArrowDown':
          return current < 0 ? 0 : Math.min(current + 1, last)
        case 'ArrowUp':
          return current < 0 ? last : Math.max(current - 1, 0)
        case 'Home':
          return 0
        case 'End':
          return last
        default:
          return undefined
      }
    })()

    const isArrow = ev.key === 'ArrowDown' || ev.key === 'ArrowUp'
    // No wrap: an arrow at either end is left alone, even from another control in that row.
    if (isArrow && next === current) {
      return
    }
    const row = next === undefined ? undefined : rows[next]
    if (!row) {
      return
    }
    const focusTarget = getFocusTarget(row)
    if (focusTarget === document.activeElement) {
      return
    }
    ev.preventDefault()
    focusTarget.focus()
  }

  const getItemRef = (index: number) => (el: HTMLElement | null) => {
    if (el) {
      itemRefs.current.set(index, el)
      return
    }
    itemRefs.current.delete(index)
  }

  return { listProps: { onKeyDown: handleKeyDown }, getItemRef }
}
